import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import type { BreadcrumbSegment } from '@/contexts/PageChromeContext';
import { useBreadcrumbCollapseLevel } from '@/hooks/use-breadcrumb-collapse-level';
import { useLanguage } from '@/i18n/language-hooks';
import { formatBreadcrumbLabel, resolveBreadcrumbCollapseFlags } from '@/lib/breadcrumbLabels';
import { cn } from '@/lib/utils';
import { Fragment, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

export interface HeaderBreadcrumbItem {
  label: string;
  href?: string;
  isActive?: boolean;
}

interface HeaderBreadcrumbsProps {
  items: HeaderBreadcrumbItem[];
  extraSegments: Array<{ key: string; segment: BreadcrumbSegment }>;
  pageSegmentIsLink: boolean;
  pageLabelHref?: string | null;
  scrollToTop: () => void;
  className?: string;
}

export function HeaderBreadcrumbs({
  items,
  extraSegments,
  pageSegmentIsLink,
  pageLabelHref,
  scrollToTop,
  className,
}: HeaderBreadcrumbsProps) {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const segmentCount = items.length + extraSegments.length;
  const collapseLevel = useBreadcrumbCollapseLevel(containerRef, listRef, segmentCount);

  const formattedItems = useMemo(() => {
    const flags = resolveBreadcrumbCollapseFlags(collapseLevel, items.length);
    return items.map((item, index) => ({
      ...item,
      ...formatBreadcrumbLabel({
        label: item.label,
        index,
        total: items.length,
        ...flags,
      }),
    }));
  }, [items, collapseLevel]);

  const renderBreadcrumbItem = (
    item: (typeof formattedItems)[number],
    index: number,
    breadcrumbItems: typeof formattedItems
  ) => {
    const isLast = index === breadcrumbItems.length - 1;
    const isActive = item.isActive ?? isLast;
    const title = item.full !== item.display ? item.full : undefined;

    if (isActive && !item.href && pageSegmentIsLink) {
      return (
        <BreadcrumbLink asChild>
          <button
            type='button'
            onClick={scrollToTop}
            className='text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring inline-flex max-w-[10rem] shrink-0 cursor-pointer items-center truncate rounded-md px-1.5 py-0.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none'
            title={title ?? t('breadcrumb.scrollToTopTitle')}
          >
            {item.display}
          </button>
        </BreadcrumbLink>
      );
    }

    if (isActive && item.href && pageLabelHref) {
      return (
        <BreadcrumbLink asChild>
          <Link
            to={pageLabelHref}
            className='text-muted-foreground hover:bg-accent hover:text-foreground inline-flex max-w-[10rem] shrink-0 items-center truncate rounded-md px-1.5 py-0.5 text-sm font-medium transition-colors'
            title={title}
          >
            {item.display}
          </Link>
        </BreadcrumbLink>
      );
    }

    if (isActive || !item.href) {
      return (
        <BreadcrumbPage
          className={cn(
            'max-w-[10rem] truncate rounded-md px-1.5 py-0.5 text-sm font-medium',
            isLast && 'min-w-0'
          )}
          title={title}
        >
          {item.display}
        </BreadcrumbPage>
      );
    }

    return (
      <BreadcrumbLink asChild>
        <Link
          to={item.href}
          className='text-muted-foreground hover:bg-accent hover:text-foreground inline-flex shrink-0 items-center truncate rounded-md px-1.5 py-0.5 text-sm font-medium transition-colors'
          title={title}
        >
          {item.display}
        </Link>
      </BreadcrumbLink>
    );
  };

  if (items.length === 0 && extraSegments.length === 0) {
    return null;
  }

  return (
    <Breadcrumb ref={containerRef} className={cn('min-w-0 overflow-hidden', className)}>
      <BreadcrumbList ref={listRef} className='flex-nowrap whitespace-nowrap'>
        {formattedItems.map((item, index) => (
          <Fragment key={`${item.label}-${index}`}>
            {index > 0 && <BreadcrumbSeparator className='shrink-0' />}
            <BreadcrumbItem className='shrink-0'>
              {renderBreadcrumbItem(item, index, formattedItems)}
            </BreadcrumbItem>
          </Fragment>
        ))}
        {extraSegments.map(({ key, segment }) => (
          <Fragment key={key}>
            <BreadcrumbSeparator className='shrink-0' />
            <BreadcrumbItem className='shrink-0'>
              {segment.trigger ?? (
                <BreadcrumbPage className='max-w-[10rem] truncate rounded-md px-1.5 py-0.5 text-sm font-medium'>
                  {segment.label}
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
